import { Store } from '.';

export enum CopilotDisplayMode {
  UnhelpfulFeedback = 'UnhelpfulFeedback',
  CopilotMainView = 'CopilotMainView',
  Chatbot = 'Chatbot',
  Loading = 'Loading',
}

export enum ChatbotFlow {
  REGULAR = 'regular_flow',
  CONTACT = 'contact_flow',
}

export type CopilotSlice = {
  copilotDisplayMode: CopilotDisplayMode;
  isChatbotInitializing: boolean;
  isChatbotReady: boolean;
  hasPendingChatbotMessage: boolean;
  chatbotReferral: string | null;
  chatbotNodeId: string | null;
  chatbotFlow: ChatbotFlow;
  unreadMessagesCount: number;
  hasEverHadMessages: boolean;
  setCopilotDisplayMode: (displayMode: CopilotDisplayMode) => void;
  setIsChatbotInitializing: (isChatbotInitializing: boolean) => void;
  setIsChatbotReady: (isChatbotReady: boolean) => void;
  setHasPendingChatbotMessage: (hasPendingChatbotMessage: boolean) => void;
  setChatbotReferral: (referral: string | null) => void;
  setChatbotNodeId: (nodeId: string | null) => void;
  setChatbotFlow: (flow: ChatbotFlow) => void;
  setUnreadMessagesCount: (count: number) => void;
};

export const getCopilotInitialState = (
  set: (stateUpdater: (state: Store) => Partial<Store>) => void,
): CopilotSlice => ({
  copilotDisplayMode: CopilotDisplayMode.CopilotMainView,
  isChatbotInitializing: false,
  isChatbotReady: false,
  hasPendingChatbotMessage: false,
  chatbotReferral: null,
  chatbotNodeId: null,
  chatbotFlow: ChatbotFlow.REGULAR,
  unreadMessagesCount: 0,
  hasEverHadMessages: false,
  setCopilotDisplayMode: (copilotDisplayMode: CopilotDisplayMode) => {
    set((state: Store) => ({
      copilotSlice: {
        ...state.copilotSlice,
        copilotDisplayMode,
      },
    }));
  },
  setIsChatbotInitializing: (isChatbotInitializing: boolean) => {
    set((state: Store) => ({
      copilotSlice: {
        ...state.copilotSlice,
        isChatbotInitializing,
        copilotDisplayMode: CopilotDisplayMode.Loading,
      },
    }));
  },
  setIsChatbotReady: (isChatbotReady: boolean) => {
    set((state: Store) => ({
      copilotSlice: {
        ...state.copilotSlice,
        isChatbotReady,
        isChatbotInitializing: false,
      },
    }));
  },
  setHasPendingChatbotMessage: (hasPendingChatbotMessage: boolean) => {
    set((state: Store) => ({
      copilotSlice: {
        ...state.copilotSlice,
        hasPendingChatbotMessage,
      },
    }));
  },
  setChatbotReferral: (chatbotReferral: string | null) => {
    set((state: Store) => ({
      copilotSlice: {
        ...state.copilotSlice,
        chatbotReferral,
      },
    }));
  },
  setChatbotNodeId: (chatbotNodeId: string | null) => {
    set((state: Store) => ({
      copilotSlice: {
        ...state.copilotSlice,
        chatbotNodeId,
      },
    }));
  },
  setChatbotFlow: (chatbotFlow: ChatbotFlow) => {
    set((state: Store) => ({
      copilotSlice: {
        ...state.copilotSlice,
        chatbotFlow,
      },
    }));
  },
  setUnreadMessagesCount: (unreadMessagesCount: number) => {
    set((state: Store) => ({
      copilotSlice: {
        ...state.copilotSlice,
        unreadMessagesCount,
        hasEverHadMessages:
          state.copilotSlice.hasEverHadMessages || unreadMessagesCount > 0,
      },
    }));
  },
});
